import { inject, Injectable } from '@angular/core';
import type { MetaDefinition } from '@angular/platform-browser';
import { Meta, Title } from '@angular/platform-browser';

import type { ICommonMeta, TOpenGraphMeta, TTwitterMeta } from './social-metadata.types';

@Injectable({ providedIn: 'root' })
export class SocialMetaService {
    private readonly meta = inject(Meta);
    private readonly title = inject(Title);

    public setCommonMeta(meta: ICommonMeta): void {
        this.removeCommonMeta();
        this.title.setTitle(this.prepareMetaValue(meta.title));
        this.meta.addTag({ name: 'description', content: this.prepareMetaValue(meta.description) });
    }

    public setOpenGraphMeta(meta: TOpenGraphMeta): void {
        this.removeOpenGraphMeta();
        this.meta.addTags(this.generateOpenGraphMetaTags(meta));
    }

    public setTwitterMeta(meta: TTwitterMeta): void {
        this.removeTwitterMeta();
        this.meta.addTags(this.generateTwitterMetaTags(meta));
    }

    public removeOpenGraphMeta() {
        this.removeTagsWithSelector('property^="og:"');
        this.removeTagsWithSelector('property^="article:"');
    }

    public removeTwitterMeta() {
        this.removeTagsWithSelector('name^="twitter:"');
    }

    public removeCommonMeta() {
        this.removeTagsWithSelector('name="description"');
    }

    private generateOpenGraphMetaTags(meta: TOpenGraphMeta): MetaDefinition[] {
        return Object.keys(meta).reduce((accumulator, key) => {
            const value = meta[key];
            if (this.isSimpleOpenGraphMetaValue(value)) {
                accumulator.push({
                    property: `${this.getOpenGraphPropertyPrefix(key)}${this.splitMetaDataKey(key)}`,
                    content: this.prepareMetaValue(value),
                });
            } else if (this.isComplexOpenGraphMetaValue(value)) {
                accumulator.push(
                    ...value.reduce((accumulator, item) => {
                        accumulator.push(...this.generateOpenGraphMetaTags(item));

                        return accumulator;
                    }, [])
                );
            }

            return accumulator;
        }, []);
    }

    private generateTwitterMetaTags(meta: TTwitterMeta): MetaDefinition[] {
        return Object.keys(meta).reduce((accumulator, key) => {
            if (meta[key]) {
                accumulator.push({
                    name: `twitter:${this.splitMetaDataKey(key)}`,
                    content: this.prepareMetaValue(meta[key]),
                });
            }

            return accumulator;
        }, []);
    }

    private removeTagsWithSelector(attrSelector: string): void {
        this.meta.getTags(attrSelector).forEach((tag) => this.meta.removeTagElement(tag));
    }

    private splitMetaDataKey(key: string): string {
        return key
            .split(/(?=[A-Z])/)
            .join(':')
            .toLowerCase();
    }

    private prepareMetaValue(value: unknown): string {
        if (value instanceof Date) {
            return value.toISOString();
        } else if (Object(value) === value) {
            throw new TypeError('Meta property value must be a primitive value or an instance of Date.');
        }

        return String(value);
    }

    private getOpenGraphPropertyPrefix(key: string): string {
        return key.startsWith('article') ? '' : 'og:';
    }

    private isSimpleOpenGraphMetaValue(value: unknown): boolean {
        return !!value && !Array.isArray(value);
    }

    private isComplexOpenGraphMetaValue(value: unknown): boolean {
        return Array.isArray(value);
    }
}
